/**
 * ExecutionListItem - Shared component for rendering execution log items
 * 
 * This component is used by both:
 * 1. Stateful (autonomous) workflows - entire run action list
 * 2. OMT (chatMessage) workflows - per-message iteration action list
 * 
 * The goal is to provide consistent UX for viewing workflow execution details
 * including support for scopes (If, Switch, Foreach, Until), nested actions,
 * and expandable items.
 */

import React from 'react';
import { Text, Spinner, mergeClasses, makeStyles, tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';
import {
  ChevronDownRegular,
  ChevronRightRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowCircleRightRegular,
  CircleRegular,
  SubtractCircleRegular,
  WarningRegular,
  FolderRegular,
  ArrowRepeatAllRegular,
} from '@fluentui/react-icons';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { formatDuration } from '@/utils/dateTime';

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  statusIcon: {
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    flexShrink: 0,
  },
  statusIconSucceeded: {
    color: ottoTokens.statusSucceeded,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  statusIconFailed: {
    color: ottoTokens.statusFailed,
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
  statusIconRunning: {
    color: ottoTokens.statusRunning,
    '@media (forced-colors: active)': {
      color: 'Highlight',
    },
  },
  statusIconSkipped: {
    color: ottoTokens.statusSkipped,
    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
  statusIconCancelled: {
    color: ottoTokens.statusCancelled,
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
  statusIconPending: {
    color: ottoTokens.statusPending,
    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
  scopeIcon: {
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    marginRight: tokens.spacingHorizontalXS,
  },
  executionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    width: '100%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    borderRadius: tokens.borderRadiusMedium,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  executionItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  executionItemHighlight: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  executionItemFailed: {
    '& > *:first-child': {
      color: ottoTokens.statusFailed,
    },
  },
  executionItemSkipped: {
    opacity: 0.6,
  },
  executionItemCancelled: {
    // Cancellation is intentional but the action is still meaningful — don't
    // dim like skipped. Just color the leading icon (handled by statusIconCancelled).
  },
  executionItemScope: {},
  executionItemAgent: {},
  executionItemAction: {},
  itemContent: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  itemName: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  itemNameSelected: {
    fontWeight: tokens.fontWeightSemibold,
  },
  durationBadge: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalSNudge}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  repetitionBadge: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalSNudge}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  // Bare-icon chevron toggle. Sized identically to the StatusIcon
  // (`spacingHorizontalL`) so it sits in the same column on expandable
  // rows as the status icon does on leaf rows. The row's own status
  // icon then renders to the right of the chevron and is intentionally
  // shifted right on expandable rows.
  treeExpandToggle: {
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: tokens.colorNeutralForeground2,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusSmall,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '1px',
    },
    '& svg': {
      width: tokens.spacingHorizontalM,
      height: tokens.spacingHorizontalM,
    },
  },
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    color: tokens.colorNeutralForeground3,
  },
});

// =============================================================================
// Shared Types
// =============================================================================

export interface ExecutionItem {
  /** Unique ID for the item */
  nodeId: string;
  /** Display name */
  name: string;
  /** Execution status */
  status: NodeExecutionStatus;
  /** Action type (e.g., 'If', 'Compose', 'Foreach', 'Agent') */
  type?: string;
  /** Duration in milliseconds */
  duration?: number;
  /** Start time ISO string */
  startTime?: string;
  /** End time ISO string */
  endTime?: string;
  /** Error information */
  error?: { code: string; message: string };
  /** Whether this is a scope (If, Switch, Foreach, Until, Scope) */
  isScope?: boolean;
  /** Number of children/repetitions (for scopes) */
  childCount?: number;
}

export interface ExecutionListItemProps {
  /** The execution item to render */
  item: ExecutionItem;
  /** Whether this item is currently selected */
  isSelected: boolean;
  /** Whether this item is highlighted (e.g., from canvas hover) */
  isHighlighted?: boolean;
  /** Click handler */
  onClick: () => void;
  /** Ref callback for scroll-to behavior */
  itemRef?: (el: HTMLElement | null) => void;
  /** Whether this item is expanded (for expandable items) */
  isExpanded?: boolean;
  /** Toggle expanded state */
  onToggleExpand?: () => void;
  /** Whether children are loading */
  isLoadingChildren?: boolean;
  /** Children elements to render when expanded */
  children?: React.ReactNode;
  /** Indentation level (0 = root) */
  indentLevel?: number;
  /** Additional CSS class */
  className?: string;
}

// =============================================================================
// Helper Components
// =============================================================================

/**
 * Status icon based on execution status
 */
export const StatusIcon: React.FC<{ status: NodeExecutionStatus }> = ({ status }) => {
  const styles = useStyles();
  const statusClass = {
    succeeded: styles.statusIconSucceeded,
    failed: styles.statusIconFailed,
    running: styles.statusIconRunning,
    skipped: styles.statusIconSkipped,
    cancelled: styles.statusIconCancelled,
    pending: styles.statusIconPending,
  }[status] || styles.statusIconPending;
  const iconClass = mergeClasses(styles.statusIcon, statusClass);
  
  switch (status) {
    case 'succeeded':
      return <CheckmarkCircleRegular className={iconClass} />;
    case 'failed':
      return <DismissCircleRegular className={iconClass} />;
    case 'running':
      return <ArrowCircleRightRegular className={iconClass} />;
    case 'skipped':
      return <SubtractCircleRegular className={iconClass} />;
    case 'cancelled':
      return <WarningRegular className={iconClass} />;
    case 'pending':
    default:
      return <CircleRegular className={iconClass} />;
  }
};

/**
 * Check if a node type is a scope type
 */
export const isScopeNodeType = (nodeType?: string): boolean => {
  if (!nodeType) return false;
  const scopeTypes = ['if', 'switch', 'foreach', 'until', 'scope'];
  return scopeTypes.includes(nodeType.toLowerCase());
};

/**
 * Returns true only for scopes whose runtime executions produce iterations
 * (Foreach and Until). Conditional/Switch/Scope actions run once and have no
 * iteration concept, so the iteration UI (chevron, "No iterations found",
 * repetition list) should be suppressed for them.
 */
export const isIteratingScopeNodeType = (nodeType?: string): boolean => {
  if (!nodeType) return false;
  const t = nodeType.toLowerCase();
  return t.includes('foreach') || t.includes('until');
};

/**
 * Get the appropriate icon for a scope type
 */
const ScopeIcon: React.FC<{ nodeType: string | undefined }> = ({ nodeType }) => {
  const styles = useStyles();
  
  if (!nodeType) return <FolderRegular className={styles.scopeIcon} />;
  
  if (nodeType.toLowerCase().includes('foreach') || nodeType.toLowerCase().includes('until')) {
    return <ArrowRepeatAllRegular className={styles.scopeIcon} />;
  }
  return <FolderRegular className={styles.scopeIcon} />;
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * ExecutionListItem - A single item in the execution log
 * 
 * Supports:
 * - Simple actions (click to select)
 * - Expandable scopes (If, Switch, Foreach, Until) with children
 * - Loading states for async children
 * - Selection and highlight states
 */
export const ExecutionListItem: React.FC<ExecutionListItemProps> = ({
  item,
  isSelected,
  isHighlighted = false,
  onClick,
  itemRef,
  isExpanded = false,
  onToggleExpand,
  isLoadingChildren = false,
  children,
  indentLevel = 0,
  className,
}) => {
  const styles = useStyles();
  const isExpandable = item.isScope && onToggleExpand;
  const showScopeIcon = item.isScope;
  
  // Determine item styling based on type
  const getItemTypeClass = () => {
    if (item.isScope) return styles.executionItemScope;
    if (item.type?.toLowerCase() === 'agent') return styles.executionItemAgent;
    return styles.executionItemAction;
  };

  return (
    <div 
      role="treeitem" 
      aria-expanded={isExpandable ? isExpanded : undefined}
      style={{ marginLeft: indentLevel > 0 ? `${indentLevel * 16}px` : undefined }}
    >
      {/* Main item row.
          Rendered as a <div role="button"> rather than a real <button> so the
          nested expand toggle (chevron) below remains valid HTML — nesting a
          <button> inside another <button> is invalid and browsers reparent
          or break focus/click for the inner control. We keep full keyboard
          support: Enter and Space activate the row, matching native button
          semantics. */}
      <div
        ref={itemRef}
        role="button"
        tabIndex={0}
        className={mergeClasses(
          styles.executionItem,
          getItemTypeClass(),
          item.status === 'failed' && styles.executionItemFailed,
          item.status === 'skipped' && styles.executionItemSkipped,
          item.status === 'cancelled' && styles.executionItemCancelled,
          isSelected && styles.executionItemSelected,
          isHighlighted && styles.executionItemHighlight,
          className
        )}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        aria-selected={isSelected}
      >
        {/* Expand/collapse toggle for expandable items. Rendered as a bare
            16×16 icon (matching the StatusIcon footprint) and placed in
            the natural first-icon slot so the chevron lines up with the
            status icons of leaf rows in the same vertical column. The
            row's own status icon is intentionally pushed right on
            expandable rows — the chevron takes the leading slot. */}
        {isExpandable && (
          <button
            type="button"
            className={styles.treeExpandToggle}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            onKeyDown={(e) => {
              // The parent <div role="button"> also listens for Enter/Space
              // to activate row selection. Without this guard, a keyboard
              // user pressing Enter/Space on the expand toggle would BOTH
              // toggle expansion AND select the row. Stop propagation so
              // the chevron is independently keyboard-toggleable. (The
              // browser still fires the chevron's native button onClick
              // for Enter/Space.)
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
          </button>
        )}

        {/* Scope icon */}
        {showScopeIcon && <ScopeIcon nodeType={item.type} />}

        {/* Status icon */}
        <StatusIcon status={item.status} />

        {/* Item name */}
        <div className={styles.itemContent}>
          <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>
            {item.name}
          </Text>
        </div>

        {/* Child count badge for scopes */}
        {item.isScope && item.childCount !== undefined && item.childCount > 0 && (
          <Text className={styles.repetitionBadge}>
            {item.childCount}
          </Text>
        )}

        {/* Duration badge */}
        {!isExpandable && item.duration !== undefined && (
          <Text className={styles.durationBadge}>
            {formatDuration(item.duration, { style: 'execution', fallback: '--' })}
          </Text>
        )}
      </div>

      {/* Loading indicator for children */}
      {isExpandable && isExpanded && isLoadingChildren && (
        <div className={styles.loadingIndicator} style={{ marginLeft: `${(indentLevel + 1) * 16}px` }}>
          <Spinner size="extra-tiny" />
          <Text>Loading...</Text>
        </div>
      )}

      {/* Children (when expanded) */}
      {isExpandable && isExpanded && !isLoadingChildren && children}
    </div>
  );
};

// =============================================================================
// Action List Component
// =============================================================================

export interface ActionListProps {
  /** Array of execution items to render */
  items: ExecutionItem[];
  /** Currently selected node ID */
  selectedNodeId: string | null;
  /** Highlighted node ID (from canvas hover) */
  highlightedNodeId?: string | null;
  /** Click handler - receives the nodeId */
  onItemSelect: (nodeId: string) => void;
  /** Ref map for scroll-to behavior */
  itemRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Indentation level for all items */
  indentLevel?: number;
}

/**
 * ActionList - Renders a flat list of execution items
 * 
 * Used for:
 * - Stateful workflow action list
 * - OMT per-message action list
 * - Scope children list
 */
export const ActionList: React.FC<ActionListProps> = ({
  items,
  selectedNodeId,
  highlightedNodeId,
  onItemSelect,
  itemRefs,
  isLoading = false,
  emptyMessage = 'No actions',
  indentLevel = 0,
}) => {
  const styles = useStyles();
  
  if (isLoading && items.length === 0) {
    return (
      <div className={styles.loadingIndicator} style={{ marginLeft: indentLevel > 0 ? `${indentLevel * 16}px` : undefined }}>
        <Spinner size="extra-tiny" />
        <Text>Loading actions...</Text>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.loadingIndicator} style={{ marginLeft: indentLevel > 0 ? `${indentLevel * 16}px` : undefined }}>
        <Text>{emptyMessage}</Text>
      </div>
    );
  }

  return (
    <>
      {items.map((item) => {
        const itemRefProp = itemRefs ? {
          itemRef: (el: HTMLElement | null) => {
            if (el) itemRefs.current.set(item.nodeId, el);
          }
        } : {};
        
        return (
          <ExecutionListItem
            key={item.nodeId}
            item={item}
            isSelected={selectedNodeId === item.nodeId}
            isHighlighted={highlightedNodeId === item.nodeId}
            onClick={() => onItemSelect(item.nodeId)}
            indentLevel={indentLevel}
            {...itemRefProp}
          />
        );
      })}
    </>
  );
};

export default ExecutionListItem;
